import { apiRequest } from '../lib/api-client';
import type { DateFormat, TimeFormat } from '../ui/format';

/** The preferences screen (context.md sections 130, 132, 206). */

export type NotificationKind = 'ASSIGNMENT' | 'MENTION' | 'DUE' | 'FINISHED' | 'FAILED' | 'SYSTEM';

export const NOTIFICATION_KINDS: NotificationKind[] = [
  'ASSIGNMENT',
  'MENTION',
  'DUE',
  'FAILED',
  'FINISHED',
  'SYSTEM',
];

export interface ProfileSettings {
  name?: string;
  locale?: string;
  timezone?: string;
  dateFormat?: DateFormat;
  timeFormat?: TimeFormat;
  emailNotifications?: Partial<Record<NotificationKind, boolean>>;
  /** What buzzes a phone: a separate decision from what arrives by e-mail (Phase 119). */
  pushNotifications?: Partial<Record<NotificationKind, boolean>>;
}

export interface SavedProfile {
  name: string;
  locale: string;
  timezone: string;
  preferences: Record<string, unknown>;
  email_notifications: Record<NotificationKind, boolean>;
  push_notifications: Record<NotificationKind, boolean>;
}

export function saveProfile(changes: ProfileSettings): Promise<SavedProfile> {
  return apiRequest('/v1/auth/profile', { method: 'PATCH', body: changes });
}

export function listTimeZones(): Promise<{ timezones: string[] }> {
  return apiRequest('/v1/auth/timezones');
}

/**
 * The zones offered first.
 *
 * Brazil has four of them and most of this product's users are in one, so the full IANA list — four
 * hundred names, mostly irrelevant — sits underneath rather than instead. The offset shown beside
 * each is computed, never stored: it changes when a government changes its mind, and a label that
 * said `-03:00` through a daylight-saving switch would be wrong for half the year.
 */
export const COMMON_ZONES = [
  'America/Sao_Paulo',
  'America/Manaus',
  'America/Cuiaba',
  'America/Belem',
  'America/Fortaleza',
  'America/Recife',
  'America/Bahia',
  'America/Rio_Branco',
  'America/Noronha',
  'UTC',
  'America/New_York',
  'America/Los_Angeles',
  'Europe/Lisbon',
  'Europe/London',
  'Europe/Madrid',
];

/** `(UTC-03:00) São Paulo` — the label people recognise, from the zone and the clock. */
export function describeZone(zone: string, locale = 'pt-BR'): string {
  const city = zone.split('/').pop()?.replace(/_/g, ' ') ?? zone;

  try {
    const parts = new Intl.DateTimeFormat(locale, {
      timeZone: zone,
      timeZoneName: 'longOffset',
    }).formatToParts(new Date());

    const offset = parts.find((part) => part.type === 'timeZoneName')?.value ?? '';

    return offset ? `(${offset.replace('GMT', 'UTC')}) ${city}` : city;
  } catch {
    return city;
  }
}
